import { useNavigate } from 'react-router-dom'
import { useUserProfile, useUserPermissions } from '../hooks/queries'
import { useCurrentOrganization } from '../hooks/queries/useOrganization'
import { useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { queryClient } from '../lib/react-query/queryClient'

/**
 * Legacy wrapper hook for fetching user data
 * @deprecated This is kept for backwards compatibility. Use useUserProfile directly instead.
 *
 * This hook wraps the new React Query useUserProfile hook and maintains
 * the original behavior of fetching user data and syncing to Zustand.
 */
export default function useGetUserData() {
  const setUserOrg = useAuthStore((state) => state.SetUserOrg)

  const navigate = useNavigate()

  // Use React Query hooks
  const { data: userData, isError: userError, refetch: refetchUser } = useUserProfile()
  const { refetch: refetchPermissions } = useUserPermissions()
  const { refetch: refetchCurrentOrg } = useCurrentOrganization()



  // Handle errors
  useEffect(() => {
    if (userError) {
      // Cancel all ongoing queries to prevent further requests
      queryClient.cancelQueries()
      // Clear React Query cache
      queryClient.clear()
      // Clear Zustand store
      useAuthStore.getState().Logout_(false)
    }
  }, [userError])

  // Handle navigation for new users
  useEffect(() => {

    if (userData) {
      setUserOrg(userData.requires_organization_setup)
    }
  }, [userData, navigate, setUserOrg])

  // Return a function that triggers refetch (maintains original API)
  const getUserData = async () => {
    try {
      // Fetch the user profile and resolve the current organization FIRST.
      // refetchCurrentOrg() sets orgId in the auth store, which both attaches
      // the X-Current-Organization header and un-gates the permissions query.
      const userResult = await refetchUser()
      await refetchCurrentOrg()

      // Permissions MUST be fetched after the org is known, otherwise the
      // header-less request makes the backend guess the org and it can silently
      // return an empty permission list (critical for feature flags / guards).
      await refetchPermissions()

      if (userResult.data) {

        return userResult.data
      } else {
        console.error('Error fetching user data')
        // Cancel all ongoing queries
        queryClient.cancelQueries()
        // Clear React Query cache
        queryClient.clear()
        // Clear Zustand store
        useAuthStore.getState().Logout_(false)
        return false
      }
    } catch (e) {
      // Cancel all ongoing queries
      queryClient.cancelQueries()
      // Clear React Query cache
      queryClient.clear()
      // Clear Zustand store
      useAuthStore.getState().Logout_(false)
      return false
    }
  }

  return getUserData
}
